/**
 * Real-time store for managing WebSocket state
 * This provides a centralized, persistent state that survives component re-renders
 */

interface RoomState {
  joined: Set<string>;
joining: Set<string>;
  lastJoinTime: Map<string, number>;
}

interface RealtimeStore {
  rooms: {
    projects: RoomState;
    tasks: RoomState;
    threads: RoomState;
    organizations: RoomState;
    users: RoomState;
    notes: RoomState;
    collections: RoomState;
  };
  pendingOperations: Array<{
    type: 'join' | 'leave';
    roomType: string;
    id: string;
    timestamp: number;
  }>;
  operationTimer: NodeJS.Timeout | null;
}

// Create a singleton store that persists across component re-renders
class RealtimeStateManager {
  private static instance: RealtimeStateManager;
  public store: RealtimeStore; // Make store accessible for type checking

  private constructor() {
    this.store = {
      rooms: {
        projects: { joined: new Set(), joining: new Set(), lastJoinTime: new Map() },
        tasks: { joined: new Set(), joining: new Set(), lastJoinTime: new Map() },
        threads: { joined: new Set(), joining: new Set(), lastJoinTime: new Map() },
        organizations: { joined: new Set(), joining: new Set(), lastJoinTime: new Map() },
        users: { joined: new Set(), joining: new Set(), lastJoinTime: new Map() },
        notes: { joined: new Set(), joining: new Set(), lastJoinTime: new Map() },
        collections: { joined: new Set(), joining: new Set(), lastJoinTime: new Map() },
      },
      pendingOperations: [],
      operationTimer: null,
    };
  }

  static getInstance(): RealtimeStateManager {
    if (!RealtimeStateManager.instance) {
      RealtimeStateManager.instance = new RealtimeStateManager();
    }
    return RealtimeStateManager.instance;
  }

  // Check if already joined a room
  hasJoined(roomType: keyof RealtimeStore['rooms'], id: string): boolean {
    return this.store.rooms[roomType]?.joined.has(id) || false;
  }

  // Check if currently joining a room
  isJoining(roomType: keyof RealtimeStore['rooms'], id: string): boolean {
    return this.store.rooms[roomType]?.joining.has(id) || false;
  }

  // Mark as joining
  markJoining(roomType: keyof RealtimeStore['rooms'], id: string): void {
    this.store.rooms[roomType].joining.add(id);
  }

  // Mark as joined
  markJoined(roomType: keyof RealtimeStore['rooms'], id: string): void {
    const room = this.store.rooms[roomType];
    room.joining.delete(id);
    room.joined.add(id);
    room.lastJoinTime.set(id, Date.now());
  }

  // Mark as left
  markLeft(roomType: keyof RealtimeStore['rooms'], id: string): void {
    const room = this.store.rooms[roomType];
    room.joining.delete(id);
    room.joined.delete(id);
    room.lastJoinTime.delete(id);
  }

  // Get all joined rooms of a type
  getJoinedRooms(roomType: keyof RealtimeStore['rooms']): string[] {
    return Array.from(this.store.rooms[roomType].joined);
  }

  // Add pending operation
  addPendingOperation(type: 'join' | 'leave', roomType: string, id: string): void {
    // Check for duplicates
    const exists = this.store.pendingOperations.some(
      op => op.type === type && op.roomType === roomType && op.id === id
    );
    
    if (!exists) {
      this.store.pendingOperations.push({
        type,
        roomType,
        id,
        timestamp: Date.now(),
      });
    }
  }

  // Get and clear pending operations
  getPendingOperations() {
    const ops = [...this.store.pendingOperations];
    this.store.pendingOperations = [];
    return ops;
  }

  // Timer management
  getTimer(): NodeJS.Timeout | null {
    return this.store.operationTimer;
  }

  setTimer(timer: NodeJS.Timeout | null): void {
    if (this.store.operationTimer && timer) {
      clearTimeout(this.store.operationTimer);
    }
    this.store.operationTimer = timer;
  }

  // Debug helper
  debug(): void {
    Object.entries(this.store.rooms).forEach(([_type, room]) => {
      if (room.joined.size > 0) {
        // Room debug logging disabled
      }
    });
  }

  // Reset everything
  reset(): void {
    Object.values(this.store.rooms).forEach(room => {
      room.joined.clear();
      room.joining.clear();
      room.lastJoinTime.clear();
    });
    this.store.pendingOperations = [];
    if (this.store.operationTimer) {
      clearTimeout(this.store.operationTimer);
      this.store.operationTimer = null;
    }
  }

  // Clear all room memberships (called on socket reconnection)
  // This is necessary because the server loses room memberships when the socket reconnects
  clearRoomMemberships(): void {
    Object.values(this.store.rooms).forEach(room => {
      room.joined.clear();
      room.joining.clear();
      // Keep lastJoinTime for debugging/diagnostics
    });
  }
}

// Export singleton instance
export const realtimeStore = RealtimeStateManager.getInstance();

// Also expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).__realtimeStore = realtimeStore;
}